import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Download, RefreshCw, Send, Trash2, Upload } from "lucide-react";
import { api, apiError, imgUrl } from "@/lib/api";

const inputCls = "border border-[#E5E5E2] px-3 py-2 text-sm outline-none focus:border-[#1A1A1A] w-full";
const DOC_STATUS_COLORS = {
  Sent: "bg-blue-50 text-blue-700", Viewed: "bg-amber-50 text-amber-700", Completed: "bg-green-50 text-green-700",
  Declined: "bg-red-50 text-red-700", Draft: "bg-gray-100 text-gray-600",
};

export default function ClientPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [record, setRecord] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState([]);
  const [vals, setVals] = useState({});
  const [sending, setSending] = useState(false);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const [cats, setCats] = useState([]);
  const [reminding, setReminding] = useState(false);
  const [recording, setRecording] = useState(false);
  const [payAmount, setPayAmount] = useState("");
  const [payMethod, setPayMethod] = useState("Zelle");
  const galleryRef = useRef(null);

  const uploadGallery = async (files) => {
    setGalleryUploading(true);
    let ok = 0;
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const up = await api.post("/admin/upload", fd);
        await api.post(`/admin/bookings/${id}/gallery`, { url: up.data.url });
        ok++;
      } catch (e) {
        toast.error(`${file.name}: ${apiError(e)}`);
      }
    }
    setGalleryUploading(false);
    if (ok) { toast.success(`${ok} photo${ok > 1 ? "s" : ""} delivered to client`); load(); }
  };

  const removeGalleryPhoto = async (pid) => {
    if (!window.confirm("Remove this photo from the client's page?")) return;
    await api.delete(`/admin/bookings/${id}/gallery/${pid}`);
    load();
  };

  const load = () => {
    api.get(`/admin/bookings/${id}/record`).then((r) => {
      setRecord(r.data);
      const b = r.data.booking;
      setVals({
        client_name: `${b.first_name} ${b.last_name}`, email: b.email, phone: b.phone,
        session_type: b.category, date: b.session_date || b.preferred_date || "",
        time: b.session_time || b.preferred_time || "", location: b.session_location || b.location || "",
        package: b.package || "", price: b.price || "", deposit: b.deposit || "",
        balance: b.balance || "", notes: "", terms: "",
      });
    }).catch((e) => { toast.error(apiError(e)); navigate("/admin"); });
    api.get("/admin/templates").then((r) => setTemplates(r.data.filter((t) => !t.archived))).catch(() => {});
    api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [id]);

  if (!record) return <div className="min-h-screen bg-[#F4F4F1]" />;
  const b = record.booking;

  const saveDetails = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    try {
      await api.put(`/admin/bookings/${id}/details`, Object.fromEntries(fd.entries()));
      toast.success("Session details saved — calendar updated");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const recalcBalance = (e) => {
    const f = e.target.form;
    const p = parseFloat((f.price.value || "").replace(/[^0-9.]/g, "")) || 0;
    const d = parseFloat((f.deposit.value || "").replace(/[^0-9.]/g, "")) || 0;
    if (p) f.balance.value = Math.max(p - d, 0).toFixed(0);
  };

  const sendDocs = async () => {
    if (!selected.length) { toast.error("Choose at least one template"); return; }
    setSending(true);
    try {
      const r = await api.post(`/admin/bookings/${id}/send-documents`, { template_ids: selected, values: vals });
      toast.success(`Sent — client page: ${r.data.portal_url}`);
      setSelected([]);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSending(false);
    }
  };

  const toggle = (tid) => setSelected((s) => (s.includes(tid) ? s.filter((x) => x !== tid) : [...s, tid]));
  const setV = (k) => (e) => setVals({ ...vals, [k]: e.target.value });

  const parseMoney = (s) => { const m = String(s || "").replace(/[^0-9.]/g, ""); return parseFloat(m) || 0; };
  const payTotal = parseMoney(b.price);
  const allPayments = b.payments || [];
  const payPaid = allPayments.length > 0
    ? allPayments.filter((p) => !p.voided).reduce((sum, p) => sum + (Number(p.amount) || 0), 0)
    : parseMoney(b.deposit);
  const payBalance = Math.max(payTotal - payPaid, 0);
  const isCanceled = (b.status || "") === "Canceled";
  const canRemind = !isCanceled && payTotal > 0 && payBalance > 0 && !!b.email;

  const sendReminder = async () => {
    setReminding(true);
    try {
      const r = await api.post(`/admin/bookings/${id}/send-payment-reminder`);
      toast.success(`Reminder sent to ${r.data.to}`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setReminding(false);
    }
  };

  const recordPayment = async () => {
    const amt = parseMoney(payAmount);
    if (!amt || amt <= 0) { toast.error("Enter an amount"); return; }
    setRecording(true);
    try {
      await api.post(`/admin/bookings/${id}/record-payment`, { amount: amt, method: payMethod, note: "" });
      toast.success("Payment recorded — balance updated");
      setPayAmount("");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setRecording(false);
    }
  };

  const voidPayment = async (pid) => {
    if (!window.confirm("Void this payment? It stays in the history marked as voided, the balance is recalculated, and no money is moved or refunded.")) return;
    try {
      await api.post(`/admin/bookings/${id}/void-payment/${pid}`);
      toast.success("Payment voided — balance updated");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const refreshDoc = async (did) => {
    try {
      await api.post(`/admin/documents/${did}/refresh-status`);
      load();
      toast.success("Status refreshed");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F4F1]" data-testid="client-record-page">
      <header className="bg-white border-b border-[#E5E5E2] sticky top-0 z-40">
        <div className="max-w-[1100px] mx-auto px-6 h-16 flex items-center justify-between">
          <button onClick={() => navigate("/admin")} data-testid="client-back"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-[#7A7A7A] hover:text-[#1A1A1A]">
            <ArrowLeft size={14} /> Dashboard
          </button>
          <p className="font-serif text-xl italic">Bahare <span className="text-xs font-sans not-italic text-[#7A7A7A] uppercase tracking-[0.2em]">Client Record</span></p>
        </div>
      </header>

      <main className="max-w-[1100px] mx-auto px-6 py-10 space-y-8">
        <div className="bg-white border border-[#E5E5E2] p-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="font-serif text-3xl">{b.first_name} {b.last_name}</h1>
              <p className="text-sm text-[#4A4A4A]">{b.email} · {b.phone}</p>
              <p className="text-xs text-[#7A7A7A] mt-1">{b.category} · requested {b.preferred_date || "—"}</p>
            </div>
            <span className="text-xs uppercase tracking-[0.15em] px-3 py-2 bg-blue-50 text-blue-700">{b.status}</span>
          </div>
        </div>

        {/* Session & payment details */}
        <form onSubmit={saveDetails} className="bg-white border border-[#E5E5E2] p-6" data-testid="client-details-form">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#7A7A7A] mb-4">Client Info</p>
          <div className="grid sm:grid-cols-3 gap-3 mb-6">
            <input name="first_name" defaultValue={b.first_name || ""} placeholder="First name" className={inputCls} data-testid="client-first-name" />
            <input name="last_name" defaultValue={b.last_name || ""} placeholder="Last name" className={inputCls} data-testid="client-last-name" />
            <input name="phone" defaultValue={b.phone || ""} placeholder="Phone" className={inputCls} data-testid="client-phone" />
            <input name="email" type="email" defaultValue={b.email || ""} placeholder="Email" className={inputCls} data-testid="client-email" />
            <select name="category" defaultValue={b.category || ""} className={inputCls} data-testid="client-category">
              <option value="">Photography type…</option>
              {cats.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
              {b.category && !cats.some((c) => c.name === b.category) && <option value={b.category}>{b.category}</option>}
            </select>
          </div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#7A7A7A] mb-4">Session & Payment</p>
          <div className="grid sm:grid-cols-3 gap-3">
            <input name="package" defaultValue={b.package || ""} placeholder="Package (e.g. Signature Maternity)" className={inputCls} data-testid="client-package" />
            <input name="price" defaultValue={b.price || ""} placeholder="Price" className={inputCls} data-testid="client-price" onChange={recalcBalance} />
            <input name="deposit" defaultValue={b.deposit || ""} placeholder="Amount paid" className={inputCls} data-testid="client-deposit" onChange={recalcBalance} />
            <input name="balance" defaultValue={b.balance || ""} placeholder="Remaining balance (auto)" className={inputCls} data-testid="client-balance" />
            <select name="payment_status" defaultValue={b.payment_status || ""} className={inputCls} data-testid="client-payment-status">
              <option value="">Payment status…</option>
              {["Unpaid", "Deposit Paid", "Paid in Full"].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <select name="payment_method" defaultValue={b.payment_method || ""} className={inputCls} data-testid="client-payment-method">
              <option value="">Payment method…</option>
              {["Cash", "Zelle", "Venmo", "Card", "Check", "Other"].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <input name="session_date" type="date" defaultValue={b.session_date || ""} className={inputCls} data-testid="client-session-date" />
            <input name="session_time" defaultValue={b.session_time || ""} placeholder="Session time" className={inputCls} />
            <input name="duration" defaultValue={b.duration || ""} placeholder="Duration (e.g. 1 hour)" className={inputCls} data-testid="client-duration" />
            <input name="session_location" defaultValue={b.session_location || ""} placeholder="Session location" className={inputCls} />
          </div>
          <div className="mt-6">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#7A7A7A] mb-2">Internal notes <span className="normal-case tracking-normal text-[#B5B5B0]">— private, only you see this</span></p>
            <textarea name="notes" defaultValue={b.notes || ""} rows={3} placeholder="Private notes about this client or session…"
              className={`${inputCls} resize-y`} data-testid="client-internal-notes" />
          </div>
          <button type="submit" className="mt-4 bg-[#1A1A1A] text-white text-xs uppercase tracking-[0.15em] px-8 py-2.5 hover:bg-[#4A4A4A] transition-colors" data-testid="client-save-details">
            Save Details
          </button>
        </form>

        {/* Payment summary + reminder */}
        <div className="bg-white border border-[#E5E5E2] p-6" data-testid="payment-reminder-panel">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#7A7A7A] mb-4">Payment</p>
          <div className="grid grid-cols-3 gap-3 mb-5">
            <div className="border border-[#EFEFEC] p-4">
              <p className="text-[10px] uppercase tracking-[0.15em] text-[#7A7A7A]">Total</p>
              <p className="font-serif text-2xl mt-1" data-testid="pay-total">${payTotal.toFixed(0)}</p>
            </div>
            <div className="border border-[#EFEFEC] p-4">
              <p className="text-[10px] uppercase tracking-[0.15em] text-[#7A7A7A]">Paid</p>
              <p className="font-serif text-2xl mt-1 text-green-700" data-testid="pay-paid">${payPaid.toFixed(0)}</p>
            </div>
            <div className="border border-[#EFEFEC] p-4">
              <p className="text-[10px] uppercase tracking-[0.15em] text-[#7A7A7A]">Balance</p>
              <p className={`font-serif text-2xl mt-1 ${payBalance > 0 ? "text-amber-700" : "text-[#1A1A1A]"}`} data-testid="pay-balance">${payBalance.toFixed(0)}</p>
            </div>
          </div>

          {(b.payments || []).length > 0 && (
            <div className="mb-5 border border-[#EFEFEC] p-4" data-testid="payment-history">
              <p className="text-[10px] uppercase tracking-[0.15em] text-[#7A7A7A] mb-2">Payment history</p>
              {b.payments.map((p, i) => (
                <div key={p.pid || i} className="flex items-center justify-between gap-3 py-1" data-testid={`payment-row-${p.pid || i}`}>
                  <p className={`text-sm ${p.voided ? "text-[#B5B5B0] line-through" : "text-[#4A4A4A]"}`}>
                    +${Number(p.amount).toFixed(0)} <span className="text-[#7A7A7A] no-underline">via {p.method || "—"} · {new Date(p.at).toLocaleDateString()}</span>
                    {p.voided && <span className="ml-2 text-[10px] uppercase tracking-[0.12em] text-red-500 no-underline inline-block" style={{ textDecoration: "none" }}>Voided</span>}
                  </p>
                  {!p.voided && p.pid && (
                    <button onClick={() => voidPayment(p.pid)} data-testid={`void-payment-${p.pid}`}
                      className="text-[10px] uppercase tracking-[0.12em] border border-red-200 text-red-600 px-3 py-1 hover:bg-red-50 transition-colors shrink-0">
                      Void
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Record a payment */}
          <div className="mb-6 border border-[#EFEFEC] p-4" data-testid="record-payment-box">
            <p className="text-[10px] uppercase tracking-[0.15em] text-[#7A7A7A] mb-3">Record a payment (e.g. after Zelle arrives)</p>
            <div className="flex flex-wrap gap-2">
              <input value={payAmount} onChange={(e) => setPayAmount(e.target.value)} type="number" min="0" placeholder="Amount $"
                className={inputCls} style={{ maxWidth: 140 }} data-testid="record-payment-amount" />
              <select value={payMethod} onChange={(e) => setPayMethod(e.target.value)} className={inputCls} style={{ maxWidth: 140 }} data-testid="record-payment-method">
                {["Zelle", "Cash", "Venmo", "Card", "Check", "Other"].map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
              <button onClick={recordPayment} disabled={recording} data-testid="record-payment-btn"
                className="text-xs uppercase tracking-[0.12em] border border-[#1A1A1A] text-[#1A1A1A] px-5 py-2 hover:bg-[#1A1A1A] hover:text-white transition-colors disabled:opacity-50">
                {recording ? "Saving…" : "Record Payment"}
              </button>
            </div>
          </div>

          {/* Send reminder */}
          <div className="border-t border-[#EFEFEC] pt-4">
            {!b.email && <p className="text-sm text-amber-700 mb-3" data-testid="reminder-no-email">This client has no email on file — add one above to send a reminder.</p>}
            {isCanceled && <p className="text-sm text-[#7A7A7A] mb-3" data-testid="reminder-canceled">This booking is canceled.</p>}
            {!isCanceled && payTotal > 0 && payBalance <= 0 && <p className="text-sm text-green-700 mb-3" data-testid="reminder-paid">Paid in full — no balance to collect.</p>}
            {!isCanceled && payTotal <= 0 && <p className="text-sm text-[#7A7A7A] mb-3" data-testid="reminder-no-price">Set a total price above to enable payment reminders.</p>}
            <div className="flex flex-wrap items-center gap-3">
              <button onClick={sendReminder} disabled={!canRemind || reminding} data-testid="send-payment-reminder-btn"
                className="inline-flex items-center gap-2 bg-[#1A1A1A] text-white text-xs uppercase tracking-[0.15em] px-8 py-3 hover:bg-[#4A4A4A] transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                <Send size={13} /> {reminding ? "Sending…" : "Send Payment Reminder"}
              </button>
              {b.last_payment_reminder_at && (
                <p className="text-xs text-[#7A7A7A]" data-testid="last-reminder-at">
                  Last reminder sent {new Date(b.last_payment_reminder_at).toLocaleString()}
                </p>
              )}
            </div>
            <p className="text-[11px] text-[#B5B5B0] mt-3">Emails your Zelle details from Settings and asks the client to pay the remaining balance. This never marks the booking as paid.</p>
          </div>
        </div>

        {/* Send documents */}
        <div className="bg-white border border-[#E5E5E2] p-6" data-testid="send-documents-panel">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#7A7A7A]">Send Forms / Contract</p>
            <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-1 ${record.docusign_connected ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700"}`} data-testid="docusign-status">
              {record.docusign_connected ? "DocuSign connected" : "DocuSign not connected — contracts save as drafts"}
            </span>
          </div>
          <div className="flex flex-wrap gap-2 mb-6">
            {templates.map((t) => (
              <button key={t.id} onClick={() => toggle(t.id)} data-testid={`send-template-${t.id}`}
                className={`text-xs px-4 py-2.5 border transition-colors ${selected.includes(t.id) ? "bg-[#1A1A1A] text-white border-[#1A1A1A]" : "border-[#E5E5E2] hover:border-[#1A1A1A]"}`}>
                {t.name}
              </button>
            ))}
          </div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#7A7A7A] mb-3">Preview — edit before sending</p>
          <div className="grid sm:grid-cols-3 gap-3 mb-4">
            <input value={vals.client_name || ""} onChange={setV("client_name")} placeholder="Client name" className={inputCls} data-testid="send-client-name" />
            <input value={vals.email || ""} onChange={setV("email")} placeholder="Email" className={inputCls} data-testid="send-email" />
            <input value={vals.phone || ""} onChange={setV("phone")} placeholder="Phone" className={inputCls} />
            <input value={vals.session_type || ""} onChange={setV("session_type")} placeholder="Session type" className={inputCls} />
            <input value={vals.date || ""} onChange={setV("date")} placeholder="Session date" className={inputCls} />
            <input value={vals.time || ""} onChange={setV("time")} placeholder="Time" className={inputCls} />
            <input value={vals.location || ""} onChange={setV("location")} placeholder="Location" className={inputCls} />
            <input value={vals.package || ""} onChange={setV("package")} placeholder="Package" className={inputCls} />
            <input value={vals.price || ""} onChange={setV("price")} placeholder="Price" className={inputCls} data-testid="send-price" />
            <input value={vals.deposit || ""} onChange={setV("deposit")} placeholder="Deposit" className={inputCls} data-testid="send-deposit" />
            <input value={vals.balance || ""} onChange={setV("balance")} placeholder="Remaining balance" className={inputCls} />
            <input value={vals.terms || ""} onChange={setV("terms")} placeholder="Special terms" className={inputCls} />
          </div>
          <textarea value={vals.notes || ""} onChange={setV("notes")} rows={2} placeholder="Additional notes for the client…" className={`${inputCls} resize-none mb-4`} />
          <button onClick={sendDocs} disabled={sending} data-testid="send-documents-btn"
            className="inline-flex items-center gap-2 bg-[#1A1A1A] text-white text-xs uppercase tracking-[0.15em] px-8 py-3 hover:bg-[#4A4A4A] transition-colors disabled:opacity-50">
            <Send size={13} /> {sending ? "Sending…" : "Send for Signature"}
          </button>
        </div>

        {/* Documents */}
        <div className="bg-white border border-[#E5E5E2] p-6" data-testid="client-documents">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#7A7A7A] mb-4">Documents ({record.documents.length})</p>
          <div className="space-y-2">
            {record.documents.map((d) => (
              <div key={d.id} data-testid={`client-doc-${d.id}`} className="flex flex-wrap items-center gap-3 border border-[#EFEFEC] p-3">
                <span className="text-xs px-2 py-1 bg-[#EFEFEC]">{d.kind === "contract" ? "Contract" : "Form"}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{d.name}</p>
                  <p className="text-xs text-[#7A7A7A]">
                    Sent {d.sent_at ? new Date(d.sent_at).toLocaleDateString() : "—"}
                    {d.signed_at && ` · Signed ${new Date(d.signed_at).toLocaleDateString()}`}
                    {d.envelope_id && ` · Envelope ${d.envelope_id.slice(0, 8)}…`}
                  </p>
                </div>
                <span className={`text-[10px] uppercase tracking-[0.12em] px-2 py-1 ${DOC_STATUS_COLORS[d.status] || "bg-gray-100 text-gray-600"}`}>{d.status}</span>
                {d.envelope_id && (
                  <button onClick={() => refreshDoc(d.id)} data-testid={`client-doc-refresh-${d.id}`} title="Refresh DocuSign status"
                    className="p-2 border border-[#E5E5E2] hover:border-[#1A1A1A]">
                    <RefreshCw size={13} />
                  </button>
                )}
                {d.kind === "contract" && (
                  <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/documents/${d.id}/pdf`} target="_blank" rel="noopener noreferrer"
                    data-testid={`client-doc-pdf-${d.id}`}
                    onClick={async (e) => {
                      e.preventDefault();
                      const r = await api.get(`/admin/documents/${d.id}/pdf`, { responseType: "blob" });
                      window.open(URL.createObjectURL(r.data), "_blank");
                    }}
                    className="p-2 border border-[#E5E5E2] hover:border-[#1A1A1A]">
                    <Download size={13} />
                  </a>
                )}
              </div>
            ))}
            {record.documents.length === 0 && <p className="text-sm text-[#7A7A7A]">No documents sent yet.</p>}
          </div>
        </div>

        {/* Delivered photos */}
        <div className="bg-white border border-[#E5E5E2] p-6" data-testid="client-gallery">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#7A7A7A] mb-4">Delivered Photos ({(b.gallery || []).length})</p>
          <p className="text-xs text-[#7A7A7A] mb-4">Upload the final session photos here — the client sees them instantly on their personal page.</p>
          <div className="flex gap-3 mb-4">
            <button type="button" onClick={() => galleryRef.current?.click()} disabled={galleryUploading}
              data-testid="gallery-upload-btn"
              className="inline-flex items-center gap-2 bg-[#1A1A1A] text-white text-xs uppercase tracking-[0.15em] px-6 py-3 hover:bg-[#4A4A4A] transition-colors disabled:opacity-50">
              <Upload size={13} /> {galleryUploading ? "Uploading…" : "Upload Photos"}
            </button>
            <input ref={galleryRef} type="file" accept="image/*" multiple hidden data-testid="gallery-file-input"
              onChange={(e) => uploadGallery([...e.target.files])} />
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {(b.gallery || []).map((p) => (
              <div key={p.id} className="relative group" data-testid={`gallery-photo-${p.id}`}>
                <img src={imgUrl(p.url)} alt={p.caption || "Delivered photo"} className="w-full aspect-square object-cover" loading="lazy" />
                <button onClick={() => removeGalleryPhoto(p.id)} data-testid={`gallery-delete-${p.id}`}
                  className="absolute top-1.5 right-1.5 bg-white/90 p-1.5 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Change requests */}
        {(record.change_requests || []).length > 0 && (
          <div className="bg-white border border-[#E5E5E2] p-6" data-testid="client-change-requests">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#7A7A7A] mb-4">Change Requests</p>
            {record.change_requests.map((cr) => (
              <div key={cr.id} className="flex flex-wrap items-center gap-3 border-b border-[#EFEFEC] last:border-0 py-3" data-testid={`admin-change-${cr.id}`}>
                <p className="text-sm flex-1">
                  Requested <span className="font-medium">{cr.requested_date}{cr.requested_time ? ` · ${cr.requested_time}` : ""}</span>
                  {cr.message && <span className="text-[#7A7A7A]"> — {cr.message}</span>}
                </p>
                <span className={`text-[10px] uppercase tracking-[0.1em] px-2 py-1 ${cr.status === "Approved" ? "bg-green-50 text-green-700" : cr.status === "Declined" ? "bg-red-50 text-red-600" : "bg-amber-50 text-amber-700"}`}>{cr.status}</span>
                {cr.status === "New" && (
                  <div className="flex gap-2">
                    <button onClick={async () => { await api.post(`/admin/change-requests/${cr.id}/approve`); load(); toast.success("Approved — session updated"); }}
                      data-testid={`change-approve-${cr.id}`}
                      className="text-xs uppercase tracking-[0.12em] bg-[#1A1A1A] text-white px-4 py-2 hover:bg-[#4A4A4A]">Approve</button>
                    <button onClick={async () => { await api.post(`/admin/change-requests/${cr.id}/decline`); load(); }}
                      data-testid={`change-decline-${cr.id}`}
                      className="text-xs uppercase tracking-[0.12em] border border-[#E5E5E2] px-4 py-2 hover:border-[#1A1A1A]">Decline</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Communication log */}
        <div className="bg-white border border-[#E5E5E2] p-6">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#7A7A7A] mb-4">Email History</p>
          {(b.email_log || []).length === 0 && <p className="text-sm text-[#7A7A7A]">No emails sent from the dashboard yet.</p>}
          {(b.email_log || []).map((l, i) => (
            <p key={i} className="text-sm text-[#4A4A4A] py-1.5 border-b border-[#EFEFEC] last:border-0">
              <span className="text-[#7A7A7A] text-xs mr-2">{new Date(l.at).toLocaleString()}</span>{l.subject} → {l.to}
            </p>
          ))}
        </div>
      </main>
    </div>
  );
}
